import {
  ArrowUp,
  CircleAlert,
  CircleStop,
  Loader2,
  LoaderCircle,
  Mic,
  Paperclip,
  Square,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FileCard } from '@shared/components/files';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui/Tooltip';
import { cn } from '@shared/components/ui/cn';
import { fileExtension, formatFileSize, isBrowserPreviewableImage } from '@shared/lib/file-format';
import { useToastStore } from '@shared/stores/toast.store';

import { useAttachmentPolicy } from '../data/attachment-policy';
import { tokenCount } from '../data/context-figures';
import { levelName } from '../data/level-text';
import type { ModelName, ModelOption } from '../data/models';
import { levelAfterPick, type Level, type ThinkingChoice } from '../data/thinking-levels';
import type { PickerPolicy } from '../data-policy/picker-policy';
import { useModelControls } from '../hooks/use-model-controls';
import { useObjectUrl } from '../hooks/use-object-url';
import { useVoiceInput } from '../hooks/use-voice-input';
import { useComposerAttachmentsStore } from '../store/composer-attachments.store';
import type {
  AttachedFile,
  AttachmentStatus,
  CompactionOutcome,
  ComposerAttachment,
} from '../types';

import { ComposerNote, type ComposerNoteKind } from './ComposerNote';
import { CHIP_LABEL_MIN_WIDTH, ModelPicker } from './ModelPicker';
import { ThinkingPicker } from './ThinkingPicker';
import { VoiceWaveform } from './VoiceWaveform';
import { ComposerBoxContext } from './composer-box';

interface ComposerProps {
  modelId: string;
  /** The chat's session may run a model set elsewhere (see shownModel). */
  sessionModelUnknown?: boolean;
  /** The chat's session is a connected session: it runs the deployment's model, nothing to pick. */
  connected?: boolean;
  /** The picked model's name rides with its id, so the chat can name it once the list omits it. */
  onModelChange: (modelId: string, name?: ModelName) => void;
  onSend: (text: string, files?: AttachedFile[]) => void;
  /** Extra classes for the outer card (e.g. max-width on the full page). */
  className?: string;
  autoFocusInput?: boolean;
  /** Locks the whole input (typing, attach, voice, model, send) — e.g. a view-only Vault file. */
  disabled?: boolean;
  draftKey?: string;
  uploads?: boolean;
  /** A reply is being written: Enter queues the message, and Stop replaces Send. */
  busy?: boolean;
  /** Stop was pressed and the reply has not ended. */
  stopping?: boolean;
  /**
   * A compaction holds the chat's session: typing, attach, a new recording and Send wait (the
   * draft stays), the placeholder says so, and Send shows, never Stop; the chips stay usable, and
   * a recording already running can be stopped.
   */
  compacting?: boolean;
  /** Stops the reply being written: the Stop button, and Esc. */
  onStop?: () => void;
  /** Up on an empty composer: the queued messages come back to edit, as one text. */
  onTakeBack?: () => Promise<string>;
  /** The chat's level (Conversation.thinking, else the user's last choice); null: none chosen. */
  thinking?: ThinkingChoice | null;
  /** Sets the chat's level. Absent: no Thinking chip; a pick writes no level and shows no note. */
  onThinkingChange?: (choice: ThinkingChoice) => void;
  /** The chat's Agentic switch; absent reads as off. */
  agentic?: boolean;
  /** Sets the chat's switch. Absent: the Thinking popover has no Agentic row. */
  onAgenticChange?: (on: boolean) => void;
  /** The chat the composer writes to: the Lowered note goes when it moves to another chat. */
  conversationId?: string;
  /** The chat's model's label and provider as picked: names it while /models no longer lists it. */
  modelName?: ModelName;
  /**
   * The token meter (DEV-4598): the first of the footer's inline-end group, before the Model
   * chip. Absent where the composer has no session (a new chat, the Vault dock).
   */
  meter?: ReactNode;
  /** How this chat's last compaction press ended; its note shows while no compaction runs. */
  compactionNote?: CompactionOutcome | null;
  /** Takes that note off: its Dismiss, and a pick that lowers the level while it shows. */
  onCompactionNoteDismiss?: () => void;
  policy?: PickerPolicy;
  waitingForPolicy?: boolean;
}

/**
 * A model pick lowered the thinking level: the picked model's label, the level it landed on,
 * and what Undo puts back (the model and the level shown before the pick).
 */
interface LoweredNote {
  model: string;
  level: Level;
  undo: { modelId: string; name?: ModelName; level: ThinkingChoice };
}

const NO_ATTACHMENTS: ComposerAttachment[] = [];

const THUMBNAIL_STATUSES: readonly AttachmentStatus[] = ['uploading', 'ready', 'failed'];

type InputModality = 'keyboard' | 'pointer';

function toAttachedFile({ id, name, size, type, file, fileRef }: ComposerAttachment): AttachedFile {
  return { id, name, size, type, file, ...(fileRef ? { fileRef } : {}) };
}

function refusalReason({ status, error }: ComposerAttachment): string | undefined {
  return status === 'failed' ? error : undefined;
}

function isEmptyFile({ status, refusalCode }: ComposerAttachment): boolean {
  return status === 'failed' && refusalCode === 'empty_file';
}

function firstSentence(text: string): string {
  const trimmed = text.trim();
  return /^.*?[.!?](?=\s|$)/s.exec(trimmed)?.[0] ?? trimmed;
}

function isRejected(attachment: ComposerAttachment): boolean {
  return (
    attachment.status === 'unsupported' ||
    attachment.status === 'too-large' ||
    isEmptyFile(attachment) ||
    refusalReason(attachment) !== undefined
  );
}

function isFileDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes('Files');
}

/**
 * Shared chat input used by both the full-page chatbot and the side panel (DEV-4575 Option C).
 * A bordered rounded card: the attached files and the textarea on top; attach and mic on the
 * footer's inline start; the token meter (DEV-4598, given as `meter`), the Model chip, the
 * Thinking chip and Send on its inline end; between them, the attachments' status line, then one
 * note: while a compaction runs, that it runs; else how this tab's compaction press ended; else,
 * while a model pick has lowered the thinking level, a note that says so, with Undo, and while the
 * chat's model is no longer offered, one that says so, with Choose model; Send waits until a model
 * is picked (the draft stays). The card measures its own width, not the viewport's: below
 * CHIP_LABEL_MIN_WIDTH (the side panel at its default width) the Model chip shows its logo only
 * and the Thinking chip's level name may truncate; the meter never gives way. Every popover
 * anchors to the card.
 */
export function Composer({
  modelId,
  sessionModelUnknown,
  connected,
  onModelChange,
  onSend,
  className,
  autoFocusInput,
  disabled = false,
  draftKey = 'default',
  uploads = true,
  busy = false,
  stopping = false,
  compacting = false,
  onStop,
  onTakeBack,
  thinking,
  onThinkingChange,
  agentic,
  onAgenticChange,
  conversationId,
  modelName,
  meter,
  compactionNote = null,
  onCompactionNoteDismiss,
  policy,
  waitingForPolicy = false,
}: ComposerProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const addToast = useToastStore((s) => s.addToast);
  const attachmentPolicy = useAttachmentPolicy();
  const draft = useComposerAttachmentsStore((s) => s.drafts[draftKey]);
  const value = useComposerAttachmentsStore((s) => s.texts[draftKey] ?? '');
  const setDraftText = useComposerAttachmentsStore((s) => s.setText);
  const addDraftFiles = useComposerAttachmentsStore((s) => s.addFiles);
  const removeDraftFile = useComposerAttachmentsStore((s) => s.remove);
  const retryDraftFile = useComposerAttachmentsStore((s) => s.retry);
  const takeDraft = useComposerAttachmentsStore((s) => s.take);
  const [dragActive, setDragActive] = useState(false);
  const [attachTooltipOpen, setAttachTooltipOpen] = useState(false);
  const dragTargets = useRef<EventTarget[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachButtonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listModalityRef = useRef<InputModality>('pointer');
  const draftKeyRef = useRef(draftKey);
  /** The note row's live region, always mounted. */
  const noteRef = useRef<HTMLDivElement>(null);
  /** The card's width; 0 until it is measured, which reads as wide. */
  const [width, setWidth] = useState(0);
  /** The note shown is too long for one 24 px line (a narrow composer), so it wraps. */
  const [noteWraps, setNoteWraps] = useState(false);

  const measure = useCallback(() => {
    const el = rootRef.current;
    if (el) setWidth(el.getBoundingClientRect().width);
    const note = noteRef.current;
    if (note) setNoteWraps(note.offsetHeight > 24);
  }, []);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (noteRef.current) observer.observe(noteRef.current);
    return () => {
      observer.disconnect();
    };
  }, [measure]);

  const focusInput = useCallback(() => {
    textareaRef.current?.focus();
  }, []);
  /** What the meter needs of the composer: the box its popover anchors to, and the textarea. */
  const box = useMemo(() => ({ boxRef: rootRef, focusInput }), [focusInput]);

  useLayoutEffect(() => {
    draftKeyRef.current = draftKey;
  }, [draftKey]);

  const appendTranscript = useCallback(
    (text: string) => {
      const key = draftKeyRef.current;
      const typed = useComposerAttachmentsStore.getState().texts[key];
      setDraftText(key, typed ? `${typed} ${text}` : text);
      textareaRef.current?.focus();
    },
    [setDraftText],
  );
  const voice = useVoiceInput(appendTranscript);

  useEffect(() => {
    if (!dragActive) return;
    const hideDropZone = () => {
      dragTargets.current = [];
      setDragActive(false);
    };
    const hideOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      hideDropZone();
    };
    const hideWhenDragEntersElsewhere = (event: globalThis.DragEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      hideDropZone();
    };
    window.addEventListener('keydown', hideOnEscape, true);
    window.addEventListener('dragenter', hideWhenDragEntersElsewhere, true);
    window.addEventListener('drop', hideDropZone);
    window.addEventListener('dragend', hideDropZone);
    return () => {
      window.removeEventListener('keydown', hideOnEscape, true);
      window.removeEventListener('dragenter', hideWhenDragEntersElsewhere, true);
      window.removeEventListener('drop', hideDropZone);
      window.removeEventListener('dragend', hideDropZone);
    };
  }, [dragActive]);

  const attachments = draft ?? NO_ATTACHMENTS;
  const atLimit = attachments.length >= attachmentPolicy.maxFiles;
  const canAttach = !disabled && !compacting && !atLimit;
  if (!canAttach && attachTooltipOpen) setAttachTooltipOpen(false);
  const uploadingCount = attachments.filter((a) => a.status === 'uploading').length;
  const rejectedCount = attachments.filter(isRejected).length;
  const failedCount = attachments.filter((a) => a.status === 'failed' && !isRejected(a)).length;
  const readyCount = attachments.filter((a) => a.status === 'ready').length;
  const pendingCount = attachments.length - readyCount;

  const limits = {
    max: attachmentPolicy.maxFiles,
    size: formatFileSize(attachmentPolicy.maxBytes, t, i18n.language),
  };
  const attachLabel = t(
    'attachments.attachTooltip',
    'Attach files — up to {{max}}, {{size}} each',
    limits,
  );
  const limitLine = atLimit
    ? t('attachments.limitReached', '{{count}} files · {{size}} — the most one message can carry', {
        count: attachments.length,
        size: formatFileSize(
          attachments.reduce((total, a) => (isRejected(a) ? total : total + a.size), 0),
          t,
          i18n.language,
        ),
      })
    : null;

  function sendStatusLine(): { error: boolean; text: string } | null {
    if (rejectedCount > 0) {
      return {
        error: true,
        text: t(
          'attachments.statusRejected',
          '{{count}} files can’t be attached. Remove them to send.',
          { count: rejectedCount },
        ),
      };
    }
    if (failedCount > 0) {
      return {
        error: true,
        text: t(
          'attachments.statusFailed',
          '{{count}} files didn’t upload. Retry or remove them to send.',
          { count: failedCount },
        ),
      };
    }
    if (uploadingCount > 0) {
      return {
        error: false,
        text: t(
          'attachments.statusUploading',
          'Uploading {{count}} files — you can send when they finish',
          { count: uploadingCount },
        ),
      };
    }
    return null;
  }
  const sendStatus = sendStatusLine();

  const controls = useModelControls(
    modelId,
    { sessionModelUnknown, connected },
    onThinkingChange ? { choice: thinking ?? null } : undefined,
    modelName,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const compact = width > 0 && width < CHIP_LABEL_MIN_WIDTH;
  /** The Lowered note: never persisted, no timer. */
  const [note, setNote] = useState<LoweredNote | null>(null);
  /** The model whose Unavailable note was dismissed: it stays hidden for that model. */
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const { unavailable } = controls;
  /** The Unavailable note shows while no Lowered note does. */
  const unavailableNote = !note && unavailable !== null && dismissedFor !== modelId;
  /**
   * The note row shows one note: while a compaction runs, that it runs (nothing clears it);
   * else how this tab's press ended; else the Lowered note, else the Unavailable one.
   */
  const shownNote = compacting
    ? 'compacting'
    : compactionNote
      ? 'compaction'
      : note
        ? 'lowered'
        : unavailableNote
          ? 'unavailable'
          : null;

  // Another chat opened in this composer drops the note. A first id is the side panel's first
  // pick creating the chat, which keeps the note it just set.
  const prevConversationId = useRef(conversationId);
  useEffect(() => {
    const prev = prevConversationId.current;
    prevConversationId.current = conversationId;
    if (prev !== undefined && prev !== conversationId) setNote(null);
  }, [conversationId]);

  // While the chat's model is unavailable, or a compaction holds the session, Send and Enter
  // wait, and the draft stays.
  const canSend =
    !disabled &&
    !compacting &&
    !waitingForPolicy &&
    unavailable === null &&
    pendingCount === 0 &&
    (value.trim().length > 0 || readyCount > 0);
  /** A reply is being written: Stop replaces Send. A compaction is not one, and has no Stop. */
  const stoppable = busy && !compacting && !waitingForPolicy && onStop !== undefined;

  /**
   * A model picked: kept on the chat, and the level it lands on stored in the chat, so the level
   * is never raised by a switch, and picking the first model again shows the lowered level.
   */
  function handlePick(option: ModelOption) {
    const { level, choice, models, shown } = controls;
    const landing = levelAfterPick(models?.thinking_scale, option.thinking, level, choice);
    // First: in the side panel with no active chat it creates the chat the level is written to.
    onModelChange(option.id, { label: option.label, groupId: option.group.id });
    if (landing && onThinkingChange) onThinkingChange({ id: landing.id, rank: landing.rank });
    // The name shown before the pick, for Undo: the listed model's, else the chat's stored one
    // (the unavailable model's, or none).
    const before: ModelName | undefined = shown
      ? { label: shown.label, groupId: shown.group.id }
      : modelName;
    // A pick that lowers the level says so, with Undo; any other pick drops an earlier note.
    const lowers = Boolean(onThinkingChange && level && landing && landing.rank < level.rank);
    setNote(
      lowers && level && landing
        ? {
            model: option.label,
            level: landing,
            undo: { modelId, name: before, level: { id: level.id, rank: level.rank } },
          }
        : null,
    );
    // Its note takes the row from a press's outcome; a compaction running keeps the row.
    if (lowers && shownNote === 'compaction') onCompactionNoteDismiss?.();
    // A pick ends the unavailable state: an Undo back to that model shows its note again.
    setDismissedFor(null);
  }

  /** Undo: the model and the level shown before the pick, in one step; Agentic stays. */
  function handleUndo() {
    if (!note) return;
    onModelChange(note.undo.modelId, note.undo.name);
    onThinkingChange?.(note.undo.level);
    setNote(null);
    textareaRef.current?.focus();
  }

  function handleDismiss() {
    if (shownNote === 'compaction') onCompactionNoteDismiss?.();
    else if (note) setNote(null);
    else setDismissedFor(modelId);
    textareaRef.current?.focus();
  }

  /** The note of how this tab's press ended: its icon and its sentence. */
  function compactionNoteOf(outcome: CompactionOutcome): { kind: ComposerNoteKind; text: string } {
    switch (outcome.kind) {
      case 'compacted':
        return {
          kind: 'compacted',
          text:
            outcome.before !== null && outcome.after !== null
              ? t(
                  'composer.noteCompacted',
                  'Conversation compacted: {{before}} → {{after}} tokens.',
                  {
                    before: tokenCount(outcome.before, i18n.language),
                    after: tokenCount(outcome.after, i18n.language),
                  },
                )
              : t('composer.noteCompactedPlain', 'Conversation compacted.'),
        };
      case 'nothing':
        return { kind: 'info', text: t('composer.noteCompactNothing', 'Nothing to compact yet.') };
      case 'internalOff':
        return {
          kind: 'info',
          text: t(
            'composer.noteCompactInternalOff',
            "Compaction on Investbanq's own model is not available yet.",
          ),
        };
      case 'declined':
        return {
          kind: 'alert',
          text: t('composer.noteCompactDeclined', 'This conversation could not be compacted.'),
        };
      case 'unavailable':
        return {
          kind: 'alert',
          text: t(
            'composer.noteCompactUnavailable',
            'Compaction is unavailable right now. Try again shortly.',
          ),
        };
      case 'failed':
        return { kind: 'alert', text: t('composer.noteCompactFailed', 'Compaction failed.') };
    }
  }

  function addFiles(files: File[]) {
    if (files.length === 0) return;
    const attachButtonHadFocus = document.activeElement === attachButtonRef.current;
    const { dropped } = addDraftFiles(draftKey, files, { uploads });
    const filled =
      (useComposerAttachmentsStore.getState().drafts[draftKey]?.length ?? 0) >=
      attachmentPolicy.maxFiles;
    if (attachButtonHadFocus && filled) textareaRef.current?.focus();
    if (dropped > 0) {
      addToast({
        variant: 'error',
        title: t('attachments.overflowTitle', '{{count}} files weren’t added', { count: dropped }),
        description: t(
          'attachments.overflowDescription',
          'One message can carry up to {{max}} files.',
          limits,
        ),
      });
    }
  }

  function attachmentCard(index: number): HTMLElement | null {
    return listRef.current?.children[index]?.querySelector<HTMLElement>('[role="group"]') ?? null;
  }

  function removeAttachment(id: string) {
    const hadFocus = listRef.current?.contains(document.activeElement) ?? false;
    const index = attachments.findIndex((a) => a.id === id);
    const neighbour =
      listModalityRef.current === 'keyboard' && index !== -1
        ? (attachmentCard(index + 1) ?? attachmentCard(index - 1))
        : null;
    removeDraftFile(draftKey, id);
    if (hadFocus) (neighbour ?? textareaRef.current)?.focus();
  }

  function retryAttachment(id: string) {
    const card = attachmentCard(attachments.findIndex((a) => a.id === id));
    const hadFocus = card?.contains(document.activeElement) ?? false;
    retryDraftFile(draftKey, id);
    if (hadFocus) (listModalityRef.current === 'keyboard' ? card : textareaRef.current)?.focus();
  }

  function handleListPointerDown() {
    listModalityRef.current = 'pointer';
  }

  function handleListKeyDown() {
    listModalityRef.current = 'keyboard';
  }

  function handleAttachClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (list && list.length > 0) addFiles(Array.from(list));
    // Reset so the same file can be selected again.
    e.target.value = '';
  }

  function handleDragEnter(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    if (!canAttach) return;
    dragTargets.current = [...dragTargets.current, event.target];
    setDragActive(true);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = canAttach ? 'copy' : 'none';
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    const card = event.currentTarget;
    const remaining = dragTargets.current.filter(
      (target) => target instanceof Node && card.contains(target),
    );
    const left = remaining.indexOf(event.target);
    if (left !== -1) remaining.splice(left, 1);
    dragTargets.current = remaining;
    if (remaining.length === 0) setDragActive(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragTargets.current = [];
    setDragActive(false);
    if (canAttach) addFiles(Array.from(event.dataTransfer.files));
  }

  function handleSend() {
    if (!canSend) return;
    const text = value.trim();
    const files = takeDraft(draftKey)
      .filter((attachment) => attachment.status === 'ready')
      .map(toAttachedFile);
    onSend(text, files.length > 0 ? files : undefined);
    setNote(null);
    textareaRef.current?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      // While a reply is being written, this queues the message behind it.
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape' && stoppable) {
      // Esc stops the reply here, and never reaches the app's Escape that closes the panel.
      e.preventDefault();
      e.stopPropagation();
      if (!stopping) onStop();
    } else if (e.key === 'ArrowUp' && !value && onTakeBack) {
      e.preventDefault();
      const key = draftKey;
      void onTakeBack().then((text) => {
        if (!text) return;
        const typed = useComposerAttachmentsStore.getState().texts[key];
        setDraftText(key, typed ? `${text}\n\n${typed}` : text);
      });
    }
  }

  return (
    <div
      ref={rootRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'relative overflow-hidden rounded-xl border bg-background shadow-[0_1px_2px_rgba(0,0,0,0.06)]',
        className,
      )}
    >
      {/* Hidden file input — triggered by the Paperclip button */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={attachmentPolicy.accept}
        className="hidden"
        aria-hidden="true"
        onChange={handleFileChange}
      />

      {attachments.length > 0 && (
        <div className="flex flex-col gap-2.5 px-3 pt-3">
          {limitLine && <p className="text-xs text-muted-foreground">{limitLine}</p>}
          <div
            ref={listRef}
            role="list"
            aria-label={t('attachments.listLabel', 'Attached files')}
            onPointerDownCapture={handleListPointerDown}
            onKeyDownCapture={handleListKeyDown}
            className="flex max-h-[248px] flex-col gap-2 overflow-y-auto [scrollbar-width:thin]"
          >
            {attachments.map((attachment) => (
              <div key={attachment.id} role="listitem">
                <ComposerAttachmentCard
                  attachment={attachment}
                  limitBytes={attachment.limitBytes ?? attachmentPolicy.maxBytes}
                  held={disabled || compacting}
                  onRemove={removeAttachment}
                  onRetry={retryAttachment}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <textarea
        ref={textareaRef}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- focusing the composer is the expected behavior on the chat surface
        autoFocus={autoFocusInput}
        value={value}
        disabled={disabled || compacting}
        onChange={(e) => {
          setDraftText(draftKey, e.target.value);
        }}
        onKeyDown={handleKeyDown}
        placeholder={
          compacting
            ? t('composer.compacting', 'Compacting…')
            : waitingForPolicy
              ? t('composer.policyWaiting', 'Choose a data policy to continue…')
              : t('composer.placeholder', 'Type your message...')
        }
        aria-label={t('composer.label', 'Message AI CIO')}
        // Three 20 px lines between 12 px above and 10 px below: the drawn 82 px, its text 12 px
        // in from the box. A note takes 34 px of it (its 24 px row and 10 px below), and 8 px more
        // when it wraps to a second line, so the box keeps its height. A block, so no line box
        // adds room under it.
        className={cn(
          'block w-full resize-none bg-transparent px-3 pb-2.5 pt-3 text-sm text-foreground outline-none placeholder:text-muted-foreground',
          shownNote ? (noteWraps ? 'h-10' : 'h-12') : 'h-[82px]',
        )}
      />

      <div role="status" aria-live="polite">
        {sendStatus && (
          <div
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-xs',
              sendStatus.error ? 'text-destructive dark:text-red-400' : 'text-muted-foreground',
            )}
          >
            {sendStatus.error ? (
              <CircleAlert className="size-4 shrink-0" aria-hidden="true" />
            ) : (
              <LoaderCircle
                className="size-4 shrink-0 animate-spin text-foreground"
                aria-hidden="true"
              />
            )}
            <span className="min-w-0 flex-1">{sendStatus.text}</span>
          </div>
        )}
      </div>

      <div ref={noteRef} role="status" aria-live="polite">
        {shownNote === 'compacting' ? (
          <ComposerNote
            kind="compacting"
            text={t('composer.noteCompacting', 'Compacting the conversation…')}
          />
        ) : shownNote === 'compaction' && compactionNote ? (
          <ComposerNote {...compactionNoteOf(compactionNote)} onDismiss={handleDismiss} />
        ) : note ? (
          <ComposerNote
            kind="lowered"
            text={t('composer.lowered', 'Thinking lowered to {{level}} for {{model}}.', {
              level: levelName(t, note.level.id, note.level.label),
              model: note.model,
            })}
            actionLabel={t('composer.undo', 'Undo')}
            onAction={handleUndo}
            onDismiss={handleDismiss}
          />
        ) : (
          unavailableNote && (
            <ComposerNote
              kind="unavailable"
              text={t('composer.unavailable', '{{model}} is unavailable right now.', {
                model: unavailable.label,
              })}
              actionLabel={t('composer.chooseModel', 'Choose model')}
              onAction={() => {
                setPickerOpen(true);
              }}
              onDismiss={handleDismiss}
            />
          )
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        {/* Inline start: attachment and mic */}
        <div className="flex shrink-0 items-center">
          <TooltipProvider>
            <Tooltip open={attachTooltipOpen} onOpenChange={setAttachTooltipOpen}>
              <TooltipTrigger asChild>
                <button
                  ref={attachButtonRef}
                  type="button"
                  onClick={handleAttachClick}
                  disabled={!canAttach}
                  className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors enabled:hover:bg-accent enabled:hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={attachLabel}
                >
                  <Paperclip className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent sideOffset={4}>{attachLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <button
            type="button"
            onClick={voice.toggle}
            // A compaction holds the mic, but never a recording it found running: the mic is
            // the one way to stop it.
            disabled={
              disabled ||
              voice.status === 'transcribing' ||
              (compacting && voice.status !== 'recording')
            }
            title={t('composer.voice', 'Voice input')}
            aria-label={t('composer.voice', 'Voice input')}
            aria-pressed={voice.status === 'recording'}
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              voice.status === 'recording'
                ? 'text-destructive hover:bg-destructive/10 dark:text-red-400'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {voice.status === 'transcribing' ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : voice.status === 'recording' ? (
              <Square className="size-3.5 fill-current" />
            ) : (
              <Mic className="size-4" />
            )}
          </button>
        </div>

        {/* Inline end: the token meter, the Model chip, the Thinking chip, and Send, or Stop
            while a reply is being written, in the same footprint, so the chips never move. 4 px
            apart, and 8 px before Send as drawn; below CHIP_LABEL_MIN_WIDTH, 4 px throughout */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1">
          {meter ? (
            <ComposerBoxContext.Provider value={box}>{meter}</ComposerBoxContext.Provider>
          ) : null}
          <ModelPicker
            controls={controls}
            disabled={disabled}
            compact={compact}
            boxRef={rootRef}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onPick={handlePick}
            policy={policy}
          />
          {onThinkingChange && (
            <ThinkingPicker
              controls={controls}
              onChoiceChange={onThinkingChange}
              agentic={agentic === true}
              onAgenticChange={onAgenticChange}
              connected={connected}
              disabled={disabled}
              compact={compact}
              boxRef={rootRef}
              onOpenChange={(open) => {
                // Closing the popover drops the note; a move inside it leaves it, so nothing
                // shifts under the open popover.
                if (!open) setNote(null);
              }}
              noteRef={noteRef}
            />
          )}
          {stoppable ? (
            // Stop asks the AI CIO to stop; the reply goes on to its stop card.
            <button
              type="button"
              onClick={onStop}
              disabled={stopping}
              aria-label={
                stopping ? t('composer.stopping', 'Stopping…') : t('composer.stop', 'Stop')
              }
              title={
                stopping
                  ? t('composer.stopping', 'Stopping…')
                  : `${t('composer.stop', 'Stop')} (Esc)`
              }
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm transition-opacity',
                !compact && 'ms-1',
                stopping && 'cursor-not-allowed opacity-50',
              )}
            >
              {stopping ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <CircleStop className="size-4" />
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              aria-label={t('composer.send', 'Send')}
              title={t('composer.send', 'Send')}
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm transition-opacity',
                !compact && 'ms-1',
                !canSend && 'cursor-not-allowed opacity-50',
              )}
            >
              <ArrowUp className="size-4" />
            </button>
          )}
        </div>
      </div>

      {voice.status === 'recording' ? (
        // Live audio track while recording (tap the mic again to stop).
        <div className="px-3 pb-2">
          <VoiceWaveform active getLevel={voice.getLevel} />
        </div>
      ) : voice.status === 'transcribing' || voice.error ? (
        <div
          className={cn(
            'px-4 pb-2 text-xs',
            voice.error ? 'text-destructive dark:text-red-400' : 'text-muted-foreground',
          )}
          role="status"
          aria-live="polite"
        >
          {voice.error ?? t('voice.transcribing', 'Transcribing…')}
        </div>
      ) : null}

      {dragActive && canAttach && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed bg-background/90 p-4 text-center text-sm text-foreground">
          {attachmentPolicy.acceptsAudio
            ? t(
                'attachments.dropZoneWithAudio',
                'Drop files to attach — PDF, Word, Excel, CSV, PowerPoint, images, audio or text. Up to {{max}} files, {{size}} each.',
                limits,
              )
            : t(
                'attachments.dropZone',
                'Drop files to attach — PDF, Word, Excel, CSV, PowerPoint, images or text. Up to {{max}} files, {{size}} each.',
                limits,
              )}
        </div>
      )}
    </div>
  );
}

interface ComposerAttachmentCardProps {
  attachment: ComposerAttachment;
  limitBytes: number;
  held: boolean;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

function ComposerAttachmentCard({
  attachment,
  limitBytes,
  held,
  onRemove,
  onRetry,
}: ComposerAttachmentCardProps) {
  const { t } = useTranslation('ai-cio');
  const { id, name, size, status, progress, file } = attachment;
  const refusal = refusalReason(attachment);
  const emptyFile = isEmptyFile(attachment);
  const showsThumbnail =
    THUMBNAIL_STATUSES.includes(status) && isBrowserPreviewableImage(fileExtension(name));
  const thumbnailUrl = useObjectUrl(showsThumbnail ? file : undefined);

  const remove = () => {
    onRemove(id);
  };

  return (
    <FileCard
      name={name}
      state={status}
      sizeBytes={size}
      progress={progress}
      limitBytes={limitBytes}
      meta={
        emptyFile
          ? t('attachments.emptyFile', 'This file is empty')
          : refusal === undefined
            ? undefined
            : t('attachments.uploadRefused', 'This file can’t be uploaded')
      }
      metaTitle={refusal === undefined ? undefined : firstSentence(refusal)}
      thumbnailUrl={thumbnailUrl}
      actionsDisabled={held}
      onCancel={remove}
      onRemove={remove}
      onRetry={
        refusal === undefined && !emptyFile
          ? () => {
              onRetry(id);
            }
          : undefined
      }
    />
  );
}
